import type { HandleParamMapping } from '@examdojo/core/widget';
import type {
  ElementDisplayConfig,
  FeatureType,
  FunctionElementDef,
  FunctionType,
  HandleBehavior,
} from '../../graphpad.model';
import type { ElementContext, ElementCreator, FunctionElement } from '../element.model';
import type { FeatureStyleNode } from '../features/feature-style.model';
import { getHandlePosition, paramsFromHandle } from '../features/handle';

/** Generic params type that allows any string keys */
export interface FunctionParams {
  [key: string]: number;
}

/**
 * Handle configurations keyed by behavior.
 * Each behavior maps to its param mapping (X/Y → param names).
 */
export type HandleConfigs = Partial<Record<HandleBehavior, HandleParamMapping>>;

/**
 * Configuration metadata for a function type.
 * Ensures type safety and consistency across all function definitions.
 *
 * Note: TParams should be the base params interface (e.g., LinearParams).
 * Uses object constraint to allow specific param interfaces without index signatures.
 */
export interface FunctionMeta<TParams extends object> {
  /** Features available for this function type */
  readonly availableFeatures: readonly FeatureType[];

  /** Handle configurations keyed by behavior */
  readonly handleConfigs: HandleConfigs;

  /** Default parameter values */
  readonly defaultParams: TParams;

  /** Parameter ranges for sliders/inputs */
  readonly defaultRanges: { readonly [K in keyof TParams]: readonly [number, number] };

  /** Default visual style (optional - falls back to ElementTheme.curve) */
  readonly defaultStyle?: FeatureStyleNode;
}

/**
 * Interface for function creators.
 * Each function type (quadratic, sine, etc.) implements this interface.
 */
export interface FunctionCreator<TParams extends FunctionParams = FunctionParams>
  extends ElementCreator<FunctionElementDef> {
  /** The function type this creator handles */
  readonly functionType: FunctionType;

  /** Default parameters for this function type */
  readonly defaultParams: TParams;

  /** Features available for this function type */
  readonly availableFeatures: readonly FeatureType[];

  /**
   * Handle configurations keyed by behavior.
   * Each behavior maps to its specific configuration.
   */
  readonly handleConfigs: HandleConfigs;

  /** Computed value keys exposed via getComputedValues() at runtime */
  readonly computedValueKeys?: readonly string[];

  /** Create the function element */
  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement;

  /**
   * Get the handle position for a given behavior.
   * Returns either static coordinates or functions for reactive positions.
   */
  getHandlePosition(behavior: HandleBehavior, params: TParams): [number, number];

  /**
   * Update params from handle position.
   * Called when a handle is dragged to compute new function parameters.
   */
  paramsFromHandle(behavior: HandleBehavior, handleX: number, handleY: number, currentParams: TParams): TParams;
}

/**
 * Base class for function creators with common functionality.
 * Provides default implementations for handle position methods based on handleConfigs.
 */
export abstract class BaseFunctionCreator<TParams extends FunctionParams = FunctionParams>
  implements FunctionCreator<TParams>
{
  abstract readonly functionType: FunctionType;
  abstract readonly defaultParams: TParams;
  abstract readonly availableFeatures: readonly FeatureType[];
  abstract readonly handleConfigs: HandleConfigs;
  readonly computedValueKeys: readonly string[] = [];

  get types(): string[] {
    return [this.functionType];
  }

  /** Available handle behaviors derived from handleConfigs */
  get availableHandles(): HandleBehavior[] {
    return Object.keys(this.handleConfigs) as HandleBehavior[];
  }

  abstract create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement;

  getHandlePosition(behavior: HandleBehavior, params: TParams): [number, number] {
    const config = this.handleConfigs[behavior];
    if (!config) {
      throw new Error(`No handle config for behavior: ${behavior}`);
    }
    return getHandlePosition(params, config);
  }

  paramsFromHandle(behavior: HandleBehavior, handleX: number, handleY: number, currentParams: TParams): TParams {
    const config = this.handleConfigs[behavior];
    if (!config) {
      return currentParams;
    }
    return paramsFromHandle(behavior, handleX, handleY, currentParams, config);
  }
}
